import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_POLLS = 8;

export default function PaymentResult({ cancelled = false }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "checking"); // checking | success | pending | error | cancelled

  const poll = useCallback(async (attempt) => {
    if (attempt >= MAX_POLLS) { setState("pending"); return; }
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      if (res.data.payment_status === "paid") { setState("success"); return; }
      if (["expired", "failed"].includes(res.data.payment_status)) { setState("error"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch (e) {
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => {
    if (cancelled) return;
    if (!sessionId) { setState("error"); return; }
    poll(0);
  }, [cancelled, sessionId, poll]);

  const CFG = {
    checking: { icon: <Loader2 className="animate-spin text-[#1E90FF]" size={56} />, title: "Confirming your payment…", desc: "Hang tight, this only takes a moment.", testid: "payment-checking" },
    success: { icon: <CheckCircle2 className="text-[#22C55E]" size={56} />, title: "You're all set! 🎉", desc: "Payment received. A 360 Web Concepts specialist will reach out shortly to kick off your project.", testid: "payment-success" },
    pending: { icon: <Loader2 className="animate-spin text-[#1E90FF]" size={56} />, title: "Still processing…", desc: "Your payment is taking a little longer. We'll email you once it's confirmed.", testid: "payment-pending" },
    error: { icon: <XCircle className="text-red-500" size={56} />, title: "Something went wrong", desc: "We couldn't confirm this payment. If you were charged, contact us and we'll sort it out.", testid: "payment-error" },
    cancelled: { icon: <XCircle className="text-white/40" size={56} />, title: "Checkout cancelled", desc: "No worries — your card was not charged. You can pick a plan whenever you're ready.", testid: "payment-cancelled" },
  }[state];

  return (
    <div className="min-h-screen grid place-items-center bg-[#0a0a0a] text-white px-5">
      <div className="max-w-md w-full border border-white/10 bg-[#0d0d0d] p-10 text-center" data-testid={CFG.testid}>
        <div className="flex justify-center mb-6">{CFG.icon}</div>
        <h1 className="font-display font-black text-3xl tracking-tighter mb-3">{CFG.title}</h1>
        <p className="text-white/55 leading-relaxed mb-8">{CFG.desc}</p>
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 bg-[#22C55E] text-black font-semibold px-6 py-3 rounded-sm hover:bg-[#1fb455] transition-colors" data-testid="payment-back-home">
          <ArrowLeft size={18} /> Back to Home
        </button>
      </div>
    </div>
  );
}
